/**
 * Which control a click inside an openable `TableCard` row belongs to.
 *
 * `TableCard`'s KEYBOARD handler has carried this rule since the row became
 * operable: "a key pressed inside a cell's own control belongs to that control
 * — opening the row underneath it as well would fire two actions from one
 * keystroke." The click handler was `onClick={() => onRowOpen(row)}` with no
 * equivalent, so the same press of the same control by a mouse ran the
 * control's handler AND the row's.
 *
 * THIS IS A GUARD, NOT A BUG REPORT. No shipped call site trips it: every
 * `TableCard` that renders a button in a cell (`ProgramDetail`'s "Remove",
 * `TeamBoard`'s "Reveal name") is a table with no `onRowOpen`, so there is no
 * row action to fire twice. What makes it worth writing anyway is that the
 * absence was already being paid for by hand — `TeamBoard.tsx` guards its
 * "Reveal name" button with `e.stopPropagation()`, a defence that only exists
 * because the row it sits in might one day open something. The next author to
 * add an action column to a drill-down table gets the keyboard behaviour for
 * free and the mouse behaviour only if they remember. Now both.
 *
 * WHY NOT THE KEYBOARD HANDLER'S OWN TEST. `handleKeyDown` uses
 * `event.target !== event.currentTarget`, which is right for a key: the row
 * carries `tabIndex={0}`, so a keystroke aimed at the row itself has the ROW as
 * its target and one aimed at a control inside a cell has the control. A click
 * never does — its target is whatever element the pointer landed on, which for
 * an ordinary click on a data cell is the `<td>` or a `<span>` inside it, never
 * the `<tr>`. Copying the keyboard test across would suppress every row click
 * in the product. So the question a click has to ask is different: not "was
 * this aimed at the row", but "is anything between the pointer and the row a
 * control in its own right".
 */
/**
 * Elements that are controls by virtue of being what they are. `<a>` is absent
 * on purpose: an anchor without `href` is not focusable and not activatable, so
 * it is decoration, and decoration must not swallow the row's click.
 */
const CONTROL_TAGS = new Set([
    'button',
    'input',
    'select',
    'textarea',
    'summary',
    'option',
    // A `<label>` click activates its associated control. Letting the row open as
    // well means one click both ticks a checkbox and navigates away from it.
    'label',
]);
/**
 * Roles that declare "activating me does something". A role is a promise to
 * assistive technology; an element making that promise owns its own clicks.
 */
const CONTROL_ROLES = new Set([
    'button',
    'link',
    'checkbox',
    'radio',
    'switch',
    'menuitem',
    'menuitemcheckbox',
    'menuitemradio',
    'tab',
    'option',
    'textbox',
    'combobox',
    'searchbox',
    'slider',
    'spinbutton',
]);
/** True when this one element owns clicks aimed at it. */
export function isOwnControl(node) {
    const tag = node.tag.toLowerCase();
    if (CONTROL_TAGS.has(tag))
        return true;
    if (tag === 'a' && node.href != null)
        return true;
    const role = node.role?.trim().toLowerCase();
    if (role && CONTROL_ROLES.has(role))
        return true;
    // An explicit non-negative tabindex is an author saying "this is a stop in
    // the tab order", which is only true of something operable. `-1` is the
    // opposite claim (focusable by script, not by the user) and does not count.
    if (node.tabIndex != null && node.tabIndex.trim() !== '') {
        const n = Number(node.tabIndex);
        if (Number.isFinite(n) && n >= 0)
            return true;
    }
    return false;
}
/**
 * `path` runs from the click target outward, stopping BEFORE the row. The row
 * itself is excluded deliberately: it carries `tabIndex={0}`, so including it
 * would make it its own control and suppress every click.
 */
export function clickBelongsToCellControl(path) {
    return path.some(isOwnControl);
}
/** Read one DOM element into the record the decision above is written over. */
function describe(el) {
    return {
        tag: el.tagName.toLowerCase(),
        role: el.getAttribute('role'),
        tabIndex: el.getAttribute('tabindex'),
        href: el.getAttribute('href'),
    };
}
/**
 * The DOM half: walk from the click target up to the row and ask the question.
 * Returns true when the row should open — i.e. nothing in between claimed the
 * click.
 */
export function rowClickOpens(target, row) {
    if (!(target instanceof Element))
        return true;
    const path = [];
    let node = target;
    while (node && node !== row) {
        path.push(describe(node));
        node = node.parentElement;
    }
    // The target was not inside the row at all (a click retargeted after the DOM
    // moved, say). Nothing to attribute it to, so leave the row's behaviour as it
    // was rather than inventing a reason to swallow it.
    return !clickBelongsToCellControl(path);
}
//# sourceMappingURL=rowClick.js.map